import { useCallback, useMemo, useState } from 'react'
import moment, { Moment } from 'moment'

import { DaysAvailabilities } from './types'
import { findAvailability } from './dayAvailability'

type NumberOfMonths = 1 | 2
type VisibleMonths = [string, string?]

const MONTH_FORMAT = 'YYYY-MM'

function buildVisibleMonths(
  visibleDate: Moment,
  numberOfMonths: NumberOfMonths
): VisibleMonths {
  const visibleMonths: VisibleMonths = [visibleDate.format(MONTH_FORMAT)]

  if (numberOfMonths === 2) {
    visibleMonths.push(moment(visibleDate).add(1, 'M').format(MONTH_FORMAT))
  }

  return visibleMonths
}

export function useVisibleMonths(
  startDate: Moment | null,
  numberOfMonths: NumberOfMonths
) {
  const [visibleMonths, setVisibleMonths] = useState<VisibleMonths>(() => {
    return buildVisibleMonths(startDate || moment(), numberOfMonths)
  })

  const updateCurrentMonth = useCallback(
    (currentMonth: Moment) => {
      setVisibleMonths(buildVisibleMonths(currentMonth, numberOfMonths))
    },
    [numberOfMonths]
  )

  return [visibleMonths, updateCurrentMonth] as const
}

export default function useDiscountsVisible(
  startDate: Moment | null,
  numberOfMonths: NumberOfMonths,
  daysData: DaysAvailabilities
) {
  const [visibleMonths, updateCurrentMonth] = useVisibleMonths(
    startDate,
    numberOfMonths
  )

  const discountsVisible = useMemo(() => {
    if (!daysData) {
      return false
    }

    if (startDate) {
      const startDateData = findAvailability({ day: startDate, daysData })
      return Boolean(startDateData?.discount)
    }

    const today = moment().format('YYYY-MM-DD')
    return Object.keys(daysData).some((date) => {
      if (date < today) {
        // We don't need to check dates in the past or after the selected
        // start date:
        return false
      }

      const dateData = daysData[date]
      return (
        dateData?.discount &&
        dateData?.available &&
        visibleMonths.some((month) => date.startsWith(month))
      )
    })
  }, [startDate, visibleMonths, daysData])

  return { discountsVisible, updateCurrentMonth }
}
